import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { getNotificationErrorMessage, inspectNotificationTemplate, listNotificationConfigs, saveNotificationConfig } from "../../../api/whatsappNotificationsApi";
import { Alert, Field, btnPrimary, btnSecondary, card, inputClass } from "../scheduling/ui";
import {
  BUTTON_INDEXES, EVENTS, LIMITS, PREFIX_VARIABLE, configToForm, formToConfig, moveItem, newMapping, validateForm, variablesForEvent,
} from "./notificationConfig";

const smallInput = "rounded-lg border border-slate-200 px-2 py-1.5 text-sm";
const iconBtn = "p-1 text-slate-500 disabled:opacity-30";

export function MappingEditor({ label, row, onChange, variables, allowPrefix }) {
  const unknown = row.kind === "variable" && row.value && !variables.includes(row.value);
  return (
    <>
      <select aria-label={`${label} source`} className={smallInput} value={row.kind}
        onChange={(e) => onChange({ kind: e.target.value, value: e.target.value === "literal" ? "" : variables[0], url_prefix: "" })}>
        <option value="variable">Variable</option>
        <option value="literal">Fixed text</option>
      </select>
      {row.kind === "variable" ? (
        <select aria-label={`${label} variable`} className={smallInput} value={row.value}
          onChange={(e) => onChange({ ...row, value: e.target.value, url_prefix: e.target.value === PREFIX_VARIABLE ? row.url_prefix : "" })}>
          {unknown && <option value={row.value}>{row.value} (not allowed)</option>}
          {variables.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      ) : (
        <input aria-label={`${label} fixed text`} className={`${smallInput} min-w-[10rem] flex-1`} value={row.value} onChange={(e) => onChange({ ...row, value: e.target.value })} />
      )}
      {allowPrefix && row.kind === "variable" && row.value === PREFIX_VARIABLE && (
        <input aria-label={`${label} URL prefix`} placeholder="Optional https:// prefix sent before the link suffix" className={`${smallInput} min-w-[12rem] flex-1`}
          value={row.url_prefix} onChange={(e) => onChange({ ...row, url_prefix: e.target.value })} />
      )}
    </>
  );
}

export function MappingList({ label, rows, onChange, variables, max }) {
  const update = (i, next) => onChange(rows.map((r, idx) => (idx === i ? next : r)));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-600">{label} <span className="font-normal text-slate-400">({rows.length}/{max})</span></span>
        <button type="button" className={btnSecondary} disabled={rows.length >= max} onClick={() => onChange([...rows, { kind: "variable", value: variables[0], url_prefix: "" }])}>
          <Plus size={14} /> Add {label.toLowerCase()} parameter
        </button>
      </div>
      {rows.length === 0 && <p className="text-xs text-slate-400">No parameters.</p>}
      {rows.map((row, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 p-2">
          <span className="w-10 text-xs font-bold text-slate-500">{`{{${i + 1}}}`}</span>
          <MappingEditor label={`${label} parameter ${i + 1}`} row={row} variables={variables} onChange={(next) => update(i, next)} />
          <button type="button" aria-label={`Move ${label} parameter ${i + 1} up`} disabled={i === 0} className={iconBtn} onClick={() => onChange(moveItem(rows, i, i - 1))}><ArrowUp size={15} /></button>
          <button type="button" aria-label={`Move ${label} parameter ${i + 1} down`} disabled={i === rows.length - 1} className={iconBtn} onClick={() => onChange(moveItem(rows, i, i + 1))}><ArrowDown size={15} /></button>
          <button type="button" aria-label={`Remove ${label} parameter ${i + 1}`} className="p-1 text-rose-600" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
}

export function EventConfigCard({ botId, event, initial, onSaved, readOnly = false }) {
  const [form, setForm] = useState(() => configToForm(event.template
    ? { ...initial, template_name: initial?.template_name || event.template } : initial));
  const [message, setMessage] = useState({ kind: "", text: "" });
  const [saving, setSaving] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [template, setTemplate] = useState(null);
  const [mappingShape, setMappingShape] = useState(null);
  const variables = variablesForEvent(event.key);
  const setMapping = (part) => (value) => setForm((f) => ({ ...f, parameter_mapping: { ...f.parameter_mapping, [part]: value } }));
  const buttons = form.parameter_mapping.buttons;
  const updateButton = (i, patch) => setMapping("buttons")(buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const nextIndex = () => BUTTON_INDEXES.find((ix) => !buttons.some((b) => String(b.index) === ix)) || "0";

  const save = async (e) => {
    e.preventDefault();
    if (saving || inspecting) return;
    const errors = validateForm(form, event.key);
    if (mappingShape) {
      if (form.parameter_mapping.header.length !== mappingShape.header) errors.push(`The loaded template requires ${mappingShape.header} header parameters.`);
      if (form.parameter_mapping.body.length !== mappingShape.body) errors.push(`The loaded template requires ${mappingShape.body} body parameters.`);
      const indexes = form.parameter_mapping.buttons.map((button) => String(button.index));
      if (indexes.length !== mappingShape.buttons.length || mappingShape.buttons.some((index) => !indexes.includes(String(index)))) {
        errors.push("URL button indexes must match the loaded template.");
      }
    }
    if (errors.length) {
      setMessage({ kind: "error", text: errors.join(" ") });
      return;
    }
    setSaving(true);
    setMessage({ kind: "", text: "" });
    try {
      const saved = await saveNotificationConfig(botId, event.key, formToConfig(form));
      if (saved?.config) setForm(configToForm(saved.config));
      setMessage({ kind: "info", text: "Saved." });
      onSaved?.();
    } catch (err) {
      setMessage({ kind: "error", text: getNotificationErrorMessage(err, "Could not save this notification.") });
    } finally {
      setSaving(false);
    }
  };

  const inspect = async () => {
    if (saving || inspecting || readOnly || !form.template_name.trim()) return;
    setInspecting(true);
    setTemplate(null);
    setMappingShape(null);
    setMessage({ kind: "", text: "" });
    try {
      const result = await inspectNotificationTemplate(botId, {
        template_name: form.template_name.trim(),
        language_code: form.language_code.trim() || "en",
        phone_number_id: form.phone_number_id.trim() || null,
      });
      const shape = result.mapping_shape;
      const slot = () => ({ kind: "variable", value: variables[0], url_prefix: "" });
      setForm((previous) => ({
        ...previous,
        parameter_mapping: {
          header: Array.from({ length: shape.header }, (_, index) => previous.parameter_mapping.header[index] || slot()),
          body: Array.from({ length: shape.body }, (_, index) => previous.parameter_mapping.body[index] || slot()),
          buttons: shape.buttons.map((index) => previous.parameter_mapping.buttons.find((button) => button.index === index) || { index, parameter: slot() }),
        },
      }));
      setTemplate(result.template);
      setMappingShape(shape);
      setMessage({ kind: "info", text: "Approved template loaded. Map every slot, then save. Nothing has been sent." });
    } catch (err) {
      setMessage({ kind: "error", text: getNotificationErrorMessage(err, "Could not load Meta template.") });
    } finally {
      setInspecting(false);
    }
  };

  return (
    <form onSubmit={save} className={`${card} space-y-4`} aria-label={`${event.label} notification`}>
      <div>
        <h3 className="text-base font-bold text-slate-900">{event.label}</h3>
        <p className="text-xs text-slate-500">{event.description}</p>
      </div>
      <Alert kind={message.kind || "error"}>{message.text}</Alert>
      <fieldset disabled={saving || inspecting || readOnly} className="space-y-4">
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
        <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
        {event.template ? "Template configuration ready (does not activate birthday sending)" : "Enable WhatsApp notification"}
      </label>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field label="Template name" hint="Approved template name from Meta WhatsApp Manager.">
          <input className={inputClass} value={form.template_name} onChange={(e) => { setTemplate(null); setMappingShape(null); setForm((f) => ({ ...f, template_name: e.target.value })); }} />
        </Field>
        <Field label="Language code" hint="Defaults to en. Must match the approved template language (e.g. en_US).">
          <input className={inputClass} value={form.language_code} placeholder="en" onChange={(e) => { setTemplate(null); setMappingShape(null); setForm((f) => ({ ...f, language_code: e.target.value })); }} />
        </Field>
        <Field label="Phone number ID (optional)" hint="Leave empty to use the only connected account.">
          <input className={inputClass} value={form.phone_number_id} onChange={(e) => { setTemplate(null); setMappingShape(null); setForm((f) => ({ ...f, phone_number_id: e.target.value })); }} />
        </Field>
      </div>
      <>
        <button type="button" className={btnSecondary} disabled={!form.template_name.trim()} onClick={inspect}>{inspecting ? "Loading template..." : "Load approved template"}</button>
        <p className="text-xs text-slate-500">Load the approved template to create the required mapping rows. Existing mappings are kept in matching positions; extra rows are removed. Review each value before saving.</p>
        {template && <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3" aria-label="Approved template content">
          <p className="text-sm font-semibold">{template.name} · {template.language} · {template.status}</p>
          <p className="text-sm" role="status">Required parameters: Header {mappingShape.header} · Body {mappingShape.body} · URL buttons {mappingShape.buttons.length}</p>
          {template.components.filter((component) => component.text).map((component) => <p key={component.type} className="whitespace-pre-wrap text-sm">{component.type}: {component.text}</p>)}
          {template.components.filter((component) => component.type === "BUTTONS").flatMap((component) => component.buttons.map((button, index) => <p key={index} className="text-sm">Button {index}: {button.text} {button.url || ""}</p>))}
        </div>}
      </>
      <p className="text-xs text-slate-500">
        Templates are designed and approved in Meta. Map each positional placeholder ({"{{1}}"}, {"{{2}}"}, …) in order. Only text parameters are supported;
        named parameters, media headers and currency/date-time types are not. When enabled, saving checks the template against Meta.
      </p>
      <MappingList label="Header" max={LIMITS.header} variables={variables} rows={form.parameter_mapping.header} onChange={setMapping("header")} />
      <MappingList label="Body" max={LIMITS.body} variables={variables} rows={form.parameter_mapping.body} onChange={setMapping("body")} />
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-600">URL buttons <span className="font-normal text-slate-400">({buttons.length}/{LIMITS.buttons})</span></span>
          <button type="button" className={btnSecondary} disabled={buttons.length >= LIMITS.buttons}
            onClick={() => setMapping("buttons")([...buttons, { index: nextIndex(), parameter: newMapping(event.key) }])}>
            <Plus size={14} /> Add URL button
          </button>
        </div>
        {buttons.length === 0 && <p className="text-xs text-slate-400">No URL button parameters.</p>}
        {buttons.map((b, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 p-2">
            <label className="text-xs font-semibold text-slate-600">Index
              <select aria-label={`Button ${i + 1} index`} className={`${smallInput} ml-2`} value={b.index} onChange={(e) => updateButton(i, { index: e.target.value })}>
                {BUTTON_INDEXES.map((ix) => (
                  <option key={ix} value={ix} disabled={ix !== String(b.index) && buttons.some((o) => String(o.index) === ix)}>{ix}</option>
                ))}
              </select>
            </label>
            <MappingEditor label={`Button ${i + 1} parameter`} row={b.parameter} variables={variables} allowPrefix onChange={(next) => updateButton(i, { parameter: next })} />
            <button type="button" aria-label={`Move button ${i + 1} up`} disabled={i === 0} className={iconBtn} onClick={() => setMapping("buttons")(moveItem(buttons, i, i - 1))}><ArrowUp size={15} /></button>
            <button type="button" aria-label={`Move button ${i + 1} down`} disabled={i === buttons.length - 1} className={iconBtn} onClick={() => setMapping("buttons")(moveItem(buttons, i, i + 1))}><ArrowDown size={15} /></button>
            <button type="button" aria-label={`Remove button ${i + 1}`} className="p-1 text-rose-600" onClick={() => setMapping("buttons")(buttons.filter((_, idx) => idx !== i))}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      {!readOnly && <div className="flex justify-end">
        <button type="submit" className={btnPrimary} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
      </div>}
      </fieldset>
    </form>
  );
}

export default function WhatsAppNotificationsPanel({ botId, readOnly = false, eventKeys }) {
  const [configs, setConfigs] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setConfigs(null);
    setError("");
    listNotificationConfigs(botId)
      .then((rows) => active && setConfigs(Object.fromEntries((rows || []).map((r) => [r.event_key, r.config]))))
      .catch((err) => active && setError(getNotificationErrorMessage(err, "Could not load WhatsApp notification settings.")));
    return () => { active = false; };
  }, [botId]);

  if (error) return <Alert>{error}</Alert>;
  if (!configs) return <p className="text-sm text-slate-500">Loading WhatsApp notifications…</p>;
  return (
    <div className="space-y-4">
      {EVENTS.filter((event) => !eventKeys || eventKeys.includes(event.key))
        .map((event) => <EventConfigCard key={`${botId}:${event.key}`} botId={botId} event={event} initial={configs[event.key]} readOnly={readOnly} />)}
    </div>
  );
}
